import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Building2, Plus, Search, Filter, Trash2, Clock, TrendingUp,
  ChevronRight, AlertTriangle, CheckCircle, Globe
} from 'lucide-react';
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import CountrySelector from '@/components/CountrySelector';
import FinancingSelector from '@/components/FinancingSelector';
import { Label } from '@/components/ui/label';
import DeleteConfirmModal from '@/components/DeleteConfirmModal';

const flagEmoji = { CA: '🇨🇦', US: '🇺🇸' };

export default function DealManager() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCountry, setFilterCountry] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteDealId, setDeleteDealId] = useState(null);
  const [deletingDeal, setDeletingDeal] = useState(false);
  
  // New deal form state
  const [newDeal, setNewDeal] = useState({
    country_code: user?.preferences?.default_country || 'CA',
    property_name: '',
    purchase_price: '',
    down_payment_percent: 20,
    financing_product_id: 'conventional_bank_ca',
    interest_rate: 5.5,
    amortization_years: 30,
    monthly_rent: '',
    vacancy_rate: 5,
    property_taxes_annual: '',
    insurance_annual: '',
    maintenance_percent: 5,
    capex_percent: 5,
    management_percent: 10
  });

  useEffect(() => {
    fetchDeals();
  }, [filterCountry]);

  const fetchDeals = async () => {
    try {
      const params = filterCountry ? `?country_code=${filterCountry}` : '';
      const response = await axios.get(`${API}/deals${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeals(response.data.deals || []);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeal = async () => {
    if (!newDeal.property_name || !newDeal.purchase_price || !newDeal.monthly_rent) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/deals`, {
        country_code: newDeal.country_code,
        analysis_type: 'rental',
        deal_input: {
          property_name: newDeal.property_name,
          purchase_price: parseFloat(newDeal.purchase_price),
          down_payment_percent: parseFloat(newDeal.down_payment_percent),
          financing_product_id: newDeal.financing_product_id,
          interest_rate: parseFloat(newDeal.interest_rate),
          amortization_years: parseInt(newDeal.amortization_years),
          monthly_rent: parseFloat(newDeal.monthly_rent),
          vacancy_rate: parseFloat(newDeal.vacancy_rate),
          property_taxes_annual: parseFloat(newDeal.property_taxes_annual || 0),
          insurance_annual: parseFloat(newDeal.insurance_annual || 0),
          maintenance_percent: parseFloat(newDeal.maintenance_percent),
          capex_percent: parseFloat(newDeal.capex_percent),
          management_percent: parseFloat(newDeal.management_percent)
        },
        tags: []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deal created successfully!');
      setShowCreateModal(false);
      navigate(`/deals/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create deal:', error);
      toast.error(error.response?.data?.detail || 'Failed to create deal');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRequest = (dealId, e) => {
    e.stopPropagation();
    setDeleteDealId(dealId);
  };

  const handleDeleteDeal = async () => {
    if (!deleteDealId) return;
    setDeletingDeal(true);
    try {
      await axios.delete(`${API}/deals/${deleteDealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deal deleted');
      setDeleteDealId(null);
      fetchDeals();
    } catch (error) {
      toast.error('Failed to delete deal');
    } finally {
      setDeletingDeal(false);
    }
  };

  const filteredDeals = deals.filter(deal => 
    deal.property_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (validation) => {
    if (!validation) return 'text-slate-400';
    if (validation.passes_all) return 'text-emerald-500';
    if (validation.errors?.length > 0) return 'text-red-500';
    return 'text-amber-500';
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">Deal Manager</h1>
            </div>
            
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Deal
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white font-['Manrope'] mb-2">Deal Manager</h1>
          <p className="text-slate-400">Track and analyze your real estate deals with version history</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              placeholder="Search deals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-700 text-white"
            />
          </div>
          <div className="w-48">
            <CountrySelector
              value={filterCountry}
              onChange={setFilterCountry}
            />
          </div>
        </div>

        {/* Deals Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          </div>
        ) : filteredDeals.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="h-16 w-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No deals yet</h3>
            <p className="text-slate-400 mb-6">Create your first deal to start analyzing properties</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Deal
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredDeals.map((deal) => (
              <div
                key={deal.id}
                onClick={() => navigate(`/deals/${deal.id}`)}
                className="glass-card rounded-xl p-5 cursor-pointer hover:border-slate-600 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="text-2xl">{flagEmoji[deal.country_code] || '🌐'}</div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-white">
                          {deal.property_name}
                        </h3>
                        {deal.deal_score && (
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            deal.deal_score.grade.startsWith('A') ? 'bg-emerald-500/20 text-emerald-400' :
                            deal.deal_score.grade.startsWith('B') ? 'bg-cyan-500/20 text-cyan-400' :
                            deal.deal_score.grade.startsWith('C') ? 'bg-amber-500/20 text-amber-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {deal.deal_score.grade}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          v{deal.current_version}
                        </span>
                        <span>{deal.analysis_type}</span>
                        <span>{new Date(deal.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Metrics */}
                    <div className="hidden sm:flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-xs text-slate-400">Score</div>
                        <div className={`font-mono font-semibold ${
                          deal.deal_score?.score >= 75 ? 'text-emerald-500' :
                          deal.deal_score?.score >= 50 ? 'text-amber-500' : 'text-red-500'
                        }`}>
                          {deal.deal_score?.score || '-'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-400">DSCR</div>
                        <div className={`font-mono font-semibold ${getStatusColor(deal.financing_validation)}`}>
                          {deal.results?.dscr?.toFixed(2)}x
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-400">Cash Flow</div>
                        <div className={`font-mono font-semibold ${deal.results?.annual_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          ${deal.results?.annual_cash_flow?.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    
                    {/* Status Icon */}
                    <div className={getStatusColor(deal.financing_validation)}>
                      {deal.financing_validation?.passes_all ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <AlertTriangle className="h-5 w-5" />
                      )}
                    </div>
                    
                    {/* Actions */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDeleteRequest(deal.id, e)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    
                    <ChevronRight className="h-5 w-5 text-slate-600" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create Deal Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Deal</DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter property details to create a new analysis
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Country & Property Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Country</Label>
                <CountrySelector
                  value={newDeal.country_code}
                  onChange={(v) => setNewDeal({...newDeal, country_code: v})}
                />
              </div>
              <div>
                <Label className="text-slate-300">Property Name *</Label>
                <Input
                  value={newDeal.property_name}
                  onChange={(e) => setNewDeal({...newDeal, property_name: e.target.value})}
                  placeholder="123 Main Street"
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>
            </div>

            {/* Financing Product */}
            <div>
              <Label className="text-slate-300">Financing Product</Label>
              <FinancingSelector
                countryCode={newDeal.country_code}
                value={newDeal.financing_product_id}
                onChange={(v) => setNewDeal({...newDeal, financing_product_id: v})}
              />
            </div>

            {/* Purchase & Financing */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">Purchase Price *</Label>
                <Input
                  type="number"
                  value={newDeal.purchase_price}
                  onChange={(e) => setNewDeal({...newDeal, purchase_price: e.target.value})}
                  placeholder="500000"
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Down Payment %</Label>
                <Input
                  type="number"
                  value={newDeal.down_payment_percent}
                  onChange={(e) => setNewDeal({...newDeal, down_payment_percent: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Interest Rate %</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newDeal.interest_rate}
                  onChange={(e) => setNewDeal({...newDeal, interest_rate: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>
            </div>

            {/* Income */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Monthly Rent *</Label>
                <Input
                  type="number"
                  value={newDeal.monthly_rent}
                  onChange={(e) => setNewDeal({...newDeal, monthly_rent: e.target.value})}
                  placeholder="3500"
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Vacancy Rate %</Label>
                <Input
                  type="number"
                  value={newDeal.vacancy_rate}
                  onChange={(e) => setNewDeal({...newDeal, vacancy_rate: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>
            </div>

            {/* Expenses */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Annual Property Taxes</Label>
                <Input
                  type="number"
                  value={newDeal.property_taxes_annual}
                  onChange={(e) => setNewDeal({...newDeal, property_taxes_annual: e.target.value})}
                  placeholder="4000"
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Annual Insurance</Label>
                <Input
                  type="number"
                  value={newDeal.insurance_annual}
                  onChange={(e) => setNewDeal({...newDeal, insurance_annual: e.target.value})}
                  placeholder="1500"
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="text-slate-300 border-slate-700">
              Cancel
            </Button>
            <Button onClick={handleCreateDeal} disabled={creating} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              {creating ? 'Creating...' : 'Create Deal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmModal
        open={Boolean(deleteDealId)}
        onOpenChange={(open) => {
          if (!open && !deletingDeal) {
            setDeleteDealId(null);
          }
        }}
        title="Delete deal?"
        description="This will permanently remove the deal and its saved versions."
        confirmLabel="Delete Deal"
        cancelLabel="Cancel"
        destructive
        loading={deletingDeal}
        onConfirm={handleDeleteDeal}
      />
    </div>
  );
}
